import React from 'react'
import clsx from 'clsx'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { MenuItem, Select, TableCell, Typography } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { Color } from 'src/AppTypes'
import { Icon } from 'src/components/atoms'
import cellStyles from 'src/components/tables/Cells/styles.module.scss'
import styles from './styles.module.scss'

export type Option = {
  value: string
  title: string
  color?: Color
  disabled?: boolean
}

export type SelectCellProps = {
  name: string
  options: Option[]
  defaultValue?: string
  placeholder?: string
  width?: number
  disabled?: boolean
  required?: boolean
} & React.StyledProps

export const SelectCell = React.memo<SelectCellProps>(
  ({
    className,
    style,
    name,
    options,
    defaultValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholder,
    width,
    disabled,
    required,
  }: SelectCellProps) => {
    const { control, register, setValue } = useFormContext()

    const handleChange = (event: SelectChangeEvent) => {
      setValue(name, event.target.value)
    }

    // NOTE: 色はローカルstateで持つと、検索フィルタ等で行がunmount/再mountされた際に
    // defaultValue(初期値)へ巻き戻ってしまうため、フォームの現在値から導出する。
    const value = useWatch({ name, defaultValue })
    const color: Color =
      options.find((o) => o.value === value)?.color ?? 'primary'

    return (
      <TableCell
        className={clsx(className, cellStyles.cell)}
        style={{ ...style }}
      >
        <Controller
          control={control}
          name={name}
          disabled={disabled}
          defaultValue={defaultValue}
          render={({ field }) => (
            <>
              <Select
                {...field}
                {...register(name)}
                style={{ width }}
                className={clsx(styles.select, styles[color])}
                classes={{
                  disabled: styles.disabled,
                }}
                sx={{
                  '& .MuiSelect-select .notranslate::after': placeholder
                    ? {
                        content: `"${placeholder}"`,
                      }
                    : {},
                }}
                defaultValue={defaultValue}
                inputProps={{
                  'aria-label': 'Without label',
                  className: styles.input,
                  classes: {
                    outlined: styles.input,
                  },
                }}
                displayEmpty
                MenuProps={{
                  classes: {
                    paper: styles.menu,
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                // eslint-disable-next-line react/no-unstable-nested-components
                IconComponent={(props) => (
                  <Icon
                    className={clsx(
                      styles.icon,
                      styles[color],
                      `material-icons ${props.className}`
                    )}
                    icon="arrowDropDown"
                  />
                )}
                onChange={handleChange}
              >
                {options.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    className={styles.option}
                    classes={{
                      selected: styles.selected,
                    }}
                    disabled={option.disabled}
                  >
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
              {required && !field.value && (
                <Typography
                  variant="body2"
                  component="p"
                  className={styles.error}
                >
                  選択してください
                </Typography>
              )}
            </>
          )}
        />
      </TableCell>
    )
  }
)

SelectCell.displayName = 'SelectCell'
